import { useCallback, useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "./useAuth";

export interface GeolocationLog {
  id: string;
  profile_id: string;
  event_type: string;
  time_entry_id: string | null;
  captured_at: string;
  latitude: number | null;
  longitude: number | null;
  accuracy_meters: number | null;
  geofence_id: string | null;
  distance_from_site_meters: number | null;
  is_within_radius: boolean | null;
  flag_reason: string | null;
  company_id: string;
  created_at: string;
  // Joined data
  employee_name?: string;
  geofence_label?: string;
}

export interface GeolocationLogsFilters {
  startDate?: string;
  endDate?: string;
  profileId?: string;
  geofenceId?: string;
  withinRadius?: boolean | null;
}

const ITEMS_PER_PAGE = 20;

export function useGeolocationLogs() {
  const { currentCompany } = useAuth();
  const companyId = currentCompany?.id;
  
  const [logs, setLogs] = useState<GeolocationLog[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [filters, setFilters] = useState<GeolocationLogsFilters>({});

  const fetchLogs = useCallback(async (page: number = 1) => {
    if (!companyId) return;
    
    setIsLoading(true);
    try {
      let query = supabase
        .from("employee_geologs")
        .select(`
          *,
          profiles!employee_geologs_profile_id_fkey(first_name, last_name),
          company_geofences(label)
        `, { count: 'exact' })
        .eq("company_id", companyId)
        .order("captured_at", { ascending: false });

      // Apply filters
      if (filters.startDate) {
        query = query.gte("captured_at", filters.startDate);
      }
      if (filters.endDate) {
        query = query.lte("captured_at", filters.endDate);
      }
      if (filters.profileId) {
        query = query.eq("profile_id", filters.profileId);
      }
      if (filters.geofenceId) {
        query = query.eq("geofence_id", filters.geofenceId);
      }
      if (filters.withinRadius !== undefined && filters.withinRadius !== null) {
        query = query.eq("is_within_radius", filters.withinRadius);
      }

      // Apply pagination
      const from = (page - 1) * ITEMS_PER_PAGE;
      const to = from + ITEMS_PER_PAGE - 1;
      query = query.range(from, to);

      const { data, error, count } = await query;
      
      if (error) throw error;
      
      if (data) {
        const transformedLogs: GeolocationLog[] = data.map((log: any) => ({
          ...log,
          employee_name: log.profiles 
            ? `${log.profiles.first_name || ''} ${log.profiles.last_name || ''}`.trim() 
            : 'Unknown',
          geofence_label: log.company_geofences?.label || 'N/A',
        }));
        
        setLogs(transformedLogs);
        setTotalCount(count || 0);
      }
    } catch (error) {
      console.error("Error fetching geolocation logs:", error);
      setLogs([]);
      setTotalCount(0);
    } finally {
      setIsLoading(false);
    }
  }, [companyId, filters]);

  useEffect(() => {
    if (companyId) {
      setCurrentPage(1);
      fetchLogs(1);
    } else {
      setLogs([]);
      setTotalCount(0);
    }
  }, [companyId, filters, fetchLogs]);

  const handlePageChange = useCallback((page: number) => {
    setCurrentPage(page);
    fetchLogs(page);
  }, [fetchLogs]);

  const exportToCSV = useCallback(() => {
    if (logs.length === 0) return;

    const headers = [
      'Employee Name',
      'Event Type',
      'Timestamp',
      'Geofence',
      'Distance (m)',
      'Within Radius',
      'Latitude',
      'Longitude',
      'Accuracy (m)',
      'Flag Reason'
    ];

    const rows = logs.map(log => [
      log.employee_name || 'Unknown',
      log.event_type,
      new Date(log.captured_at).toLocaleString(),
      log.geofence_label || 'N/A',
      log.distance_from_site_meters?.toFixed(0) || 'N/A',
      log.is_within_radius ? 'Yes' : 'No',
      log.latitude?.toFixed(6) || 'N/A',
      log.longitude?.toFixed(6) || 'N/A',
      log.accuracy_meters?.toFixed(0) || 'N/A',
      log.flag_reason || '-'
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `geolocation-logs-${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }, [logs]);

  return {
    logs,
    isLoading,
    totalCount,
    currentPage,
    itemsPerPage: ITEMS_PER_PAGE,
    totalPages: Math.ceil(totalCount / ITEMS_PER_PAGE),
    filters,
    setFilters,
    handlePageChange,
    exportToCSV,
    refresh: () => fetchLogs(currentPage),
  };
}
